import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { WarningMessage } from '../../../components/widgets';
import FormSection from '../FormSection';
import {
    ATTRIBUTION_TAB_IDS,
    POSTBACK_EVENT_TYPE_OPTIONS,
    POSTBACK_PLATFORM_OPTION,
    TRANSLATE_PREFIX,
} from '../../consts';
import PostbackTemplateCard from './PostbackTemplateCard';
import css from './PostbackTemplatesTab.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_TEMPLATES_TAB`;

const infoContent = (
    <div>
        <Translate id={`${TAB_PREFIX}.INFO_CONTENT_INTRO`} />
        <ul className={css.infoList}>
            <li>
                <strong>
                    <Translate id={`${TAB_PREFIX}.INFO_CONTENT_KEY_LABEL`} />
                </strong>{' '}
                <Translate id={`${TAB_PREFIX}.INFO_CONTENT_KEY_DESC`} />
            </li>
            <li>
                <strong>
                    <Translate id={`${TAB_PREFIX}.INFO_CONTENT_MACRO_LABEL`} />
                </strong>{' '}
                <Translate id={`${TAB_PREFIX}.INFO_CONTENT_MACRO_DESC`} />
            </li>
        </ul>
        <div>
            <strong>
                <Translate id={`${TAB_PREFIX}.INFO_CONTENT_RESULT_LABEL`} />
            </strong>{' '}
            <code>
                <Translate id={`${TAB_PREFIX}.INFO_CONTENT_RESULT_EXAMPLE`} />
            </code>
        </div>
    </div>
);

const getCombos = entries => {
    const items = [];
    POSTBACK_EVENT_TYPE_OPTIONS.forEach(eventType => {
        const entry = entries.find(e => e.event_type === eventType);
        if (!entry) return;
        POSTBACK_PLATFORM_OPTION.forEach(platform => {
            if ((entry.platforms || []).includes(platform)) {
                items.push({ eventType, platform });
            }
        });
    });
    return items;
};

const PostbackTemplatesTab = ({ readOnly = false }) => {
    const { values } = useFormikContext();
    const overallTab = values.formData?.[ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP] || {};
    const entries = overallTab.postback_events_and_platforms || [];
    const combos = getCombos(entries);

    const [expanded, setExpanded] = useState({});
    const toggleExpanded = id => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    return (
        <FormSection
            titleKey={`${TRANSLATE_PREFIX}.TABS.POSTBACK_TEMPLATES`}
            descriptionKey={`${TAB_PREFIX}.DESCRIPTION`}
            iconName="template"
        >
            <WarningMessage
                type="info"
                showIcon={false}
                collapsible
                collapsibleTitle={`${TAB_PREFIX}.INFO_TITLE`}
                message={infoContent}
            />
            {combos.length === 0 ? (
                <div className={css.emptyState}>
                    <Translate id={`${TAB_PREFIX}.EMPTY_STATE`} />
                </div>
            ) : (
                <>
                    <div className={css.templatesCount}>
                        <Translate id={`${TAB_PREFIX}.TEMPLATES_COUNT`} data={{ count: combos.length }} />
                    </div>
                    <div className={css.templatesList}>
                        {combos.map(({ eventType, platform }) => {
                            const id = `${eventType}__${platform}`;
                            return (
                                <div
                                    key={id}
                                    className={css.cardAnchor}
                                    data-error-anchor={`postback_template_${eventType}_${platform}`}
                                >
                                    <PostbackTemplateCard
                                        eventType={eventType}
                                        platform={platform}
                                        expanded={!!expanded[id]}
                                        onToggle={() => toggleExpanded(id)}
                                        readOnly={readOnly}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </FormSection>
    );
};

PostbackTemplatesTab.propTypes = {
    readOnly: PropTypes.bool,
};

export default PostbackTemplatesTab;
